/**
 * Command palette (Cmd-K) — pure model (lane B6, R4 #19).
 *
 * The Navigate section is DERIVED from the app's own sidebar config (the
 * `sidebarGroups` array handed to `createShell()`), never a second hand-kept
 * list: every visible, parameter-free route becomes a destination, so a route
 * added to the sidebar shows up in the palette automatically.
 *
 * @module lib/commandPalette
 */

import type { SidebarGroup } from "@plerity/dashboard-shell";
import type { ComponentType } from "react";

export type PaletteIcon = ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" }>;

export interface NavItem {
  /** Absolute in-app path, e.g. "/social/ideas". */
  path: string;
  label: string;
  /** Sidebar section title ("" for the top-level group). */
  section: string;
  icon?: PaletteIcon;
}

/** Visible, parameter-free sidebar routes, in sidebar order, one per path. */
export function navItemsFromSidebar(groups: SidebarGroup[]): NavItem[] {
  const seen = new Set<string>();
  const out: NavItem[] = [];
  for (const group of groups) {
    for (const route of group.routes) {
      if (route.hidden || route.path.includes(":") || route.path.includes("*")) continue;
      const path = `/${route.path.replace(/^\/+/, "")}`;
      if (seen.has(path)) continue;
      seen.add(path);
      out.push({ path, label: route.label, section: group.title, icon: route.icon as PaletteIcon | undefined });
    }
  }
  return out;
}

/** Lower-case, accent-free text for matching. */
export function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/** Every whitespace-separated term of `query` appears in `haystack`. */
export function matches(haystack: string, query: string): boolean {
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const hay = normalize(haystack);
  return terms.every((t) => hay.includes(t));
}

export function filterNavItems(items: NavItem[], query: string): NavItem[] {
  return items.filter((i) => matches(`${i.label} ${i.section} ${i.path.replace(/[/-]/g, " ")}`, query));
}

export type PaletteActionId = "new-post" | "new-design" | "inbox-needs-reply" | "switch-client";

export interface PaletteAction {
  id: PaletteActionId;
  label: string;
  keywords: string;
  /** In-app destination; `switch-client` opens the client list instead. */
  path?: string;
}

export const PALETTE_ACTIONS: PaletteAction[] = [
  { id: "new-post", label: "New post", keywords: "create compose write caption", path: "/social/studio?tab=compose" },
  { id: "new-design", label: "New design", keywords: "create canvas graphic image", path: "/social/studio?tab=create" },
  {
    id: "inbox-needs-reply",
    label: "Open inbox: needs reply",
    keywords: "comments messages dms unread reply",
    path: "/social/inbox?status=needs_reply",
  },
  { id: "switch-client", label: "Switch client…", keywords: "brand client change select" },
];

export function filterActions(actions: PaletteAction[], query: string): PaletteAction[] {
  return actions.filter((a) => matches(`${a.label} ${a.keywords}`, query));
}

/** Minimum typed characters before the live API search runs. */
export const MIN_SEARCH_CHARS = 2;
/** Debounce for the live API search. */
export const SEARCH_DEBOUNCE_MS = 200;
/** Live results per section. */
export const RESULTS_PER_SECTION = 5;

/** One-line excerpt of a post caption for a result row. */
export function excerpt(text: string | null | undefined, max = 70): string {
  const flat = (text ?? "").replace(/\s+/g, " ").trim();
  if (!flat) return "(no caption)";
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}

/** Display label for the shortcut on this platform. */
export function shortcutLabel(platform: string = typeof navigator !== "undefined" ? navigator.platform : ""): string {
  return /mac|iphone|ipad|ipod/i.test(platform) ? "⌘K" : "Ctrl K";
}

/** True for the palette's open shortcut (Cmd-K / Ctrl-K, no Shift/Alt). */
export function isPaletteShortcut(
  e: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey">
): boolean {
  return (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "k";
}
